import { Icon, MessageBar, MessageBarType, Stack, StackItem, Text } from '@fluentui/react';
import { ChangeEvent, DragEvent, forwardRef, HTMLAttributes, Ref, useState } from 'react';

import { IComponent } from '../../models/components.models';
import { FontSizes, FontWeights } from '../../themes/styles';
import { useClassNames, useTheme } from '../../utils/hooks';
import { Button } from '../Button/Button';
import { buttonStylesPrimary } from '../Button/Button.styles';
import { fileUploadStylesDefault, IFileUploadStyles } from './FileUpload.styles';

interface IFileUploadClassNames {
  root?: string;
  input?: string;
  messagebar?: string;
  remove?: string;
}

/**
 * Create a file upload component.
 * A file upload component allows a user to browse, select and upload one or more files.
 */
export interface IFileUpload extends IComponent<IFileUploadStyles>, HTMLAttributes<HTMLDivElement> {
  /** Text to be displayed alongside the component. */
  label?: string;
  /** True if the component should allow multiple files to be uploaded. */
  multiple?: boolean;
  /** List of allowed file extensions, e.g. `pdf`, `docx`, etc. */
  fileExtensions?: string[];
  /** Maximum allowed size (Mb) per file. Defaults to no limit. */
  maxFileSize?: number;
  /** Maximum allowed size (Mb) for all files combined. Defaults to no limit. */
  maxSize?: number;
  /** Callback function to be called after hitting upload button. */
  uploadCallback: (files: File[]) => void;
}

const convertMegabytesToBytes = (bytes: number) => bytes * 1024 * 1024;
export const FileUpload = forwardRef((props: IFileUpload, ref: Ref<HTMLDivElement>) => {
  const {
    className,
    styles,
    maxFileSize = 0,
    maxSize = 0,
    fileExtensions,
    multiple,
    uploadCallback,
    ...elementProps
  } = props;
  const classNames = useClassNames<IFileUploadStyles, IFileUploadClassNames>(
    'FileUpload',
    fileUploadStylesDefault,
    styles,
    className
  );
  const [isDragging, setIsDragging] = useState(false),
    [files, setFiles] = useState<File[]>([]),
    [err, setErr] = useState(''),
    [successMsg, setSuccessMsg] = useState(''),
    theme = useTheme(),
    maxFileSizeBytes = convertMegabytesToBytes(maxFileSize),
    maxSizeBytes = convertMegabytesToBytes(maxSize),
    extensions = fileExtensions ? fileExtensions.map((e) => (e.startsWith('.') ? e : `.${e}`)) : null,
    isFileTypeAllowed = (fileName: string) => {
      if (!extensions) return true;
      for (const allowedExtension of extensions) {
        if (fileName.toLowerCase().endsWith(allowedExtension.toLowerCase())) return true;
      }
      return false;
    },
    validateFiles = (fileArr: File[]) => {
      const notAllowedFiles = fileArr.filter(({ name }) => !isFileTypeAllowed(name));
      if (notAllowedFiles.length) {
        return `Not allowed extension for files: ${notAllowedFiles.map(({ name }) => name).join(', ')}.
           Allowed file extensions: ${extensions?.join(', ')}.`;
      }

      if (maxFileSizeBytes) {
        const maxSizePerFileExceededFiles = fileArr.filter(({ size }) => size > maxFileSizeBytes);
        if (maxSizePerFileExceededFiles.length) {
          return `Max file size exceeded for files: ${maxSizePerFileExceededFiles.map(({ name }) => name).join(', ')}.
             Allowed size per file: ${maxFileSize}Mb.`;
        }
      }

      if (maxSizeBytes) {
        const totalSize = fileArr.reduce((total, { size }) => total + size, 0);
        if (totalSize > maxSizeBytes) {
          return `Total max file size exceeded. Allowed size: ${maxSize}Mb.`;
        }
      }
      return;
    },
    onChange = (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length) return;
      const fileArr = Array.from(files);

      const errMsg = validateFiles(fileArr);
      if (errMsg) {
        setErr(errMsg);
        return;
      }
      uploadCallback(fileArr);
      setFiles(fileArr);
    },
    onIsDragging = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    },
    onIsNotDragging = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    },
    onDrop = (e: DragEvent) => {
      onIsNotDragging(e);
      const files = e.dataTransfer.files;
      if (!files.length || err || successMsg) return;
      if (!multiple && files.length > 1) {
        setErr('Cannot upload multiple files. Input is not set to multiple mode.');
        return;
      }
      const fileArr = Array.from(files);

      const errMsg = validateFiles(fileArr);
      if (errMsg) {
        setErr(errMsg);
        return;
      }
      uploadCallback(fileArr);
      setFiles(fileArr);
    },
    // Workaround - This event prevents onDrop from firing.
    // https://stackoverflow.com/questions/50230048/react-ondrop-is-not-firing/50230145.
    onDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    },
    onDismissError = () => setErr(''),
    onDismissSuccess = () => setSuccessMsg(''),
    removeFile = (index: number) => () => {
      files.splice(index, 1);
      uploadCallback([...files]);
      setFiles([...files]);
    },
    getUploadBodyComponent = () => {
      if (err) {
        return (
          <>
            <Text variant="xLarge">An error occured</Text>
            <MessageBar
              className={classNames.messagebar}
              messageBarType={MessageBarType.error}
              isMultiline
              onDismiss={onDismissError}
            >
              {err}
            </MessageBar>
          </>
        );
      } else if (successMsg) {
        return (
          <>
            <Text variant="xLarge">Upload successful</Text>
            <MessageBar
              className={classNames.messagebar}
              messageBarType={MessageBarType.success}
              isMultiline
              onDismiss={onDismissSuccess}
            >
              {successMsg}
            </MessageBar>
          </>
        );
      } else if (isDragging) {
        return <Text styles={{ root: { pointerEvents: 'none' } }}>Drop files anywhere within the box.</Text>;
      } else if (files.length) {
        return (
          <>
            <Text variant="xLarge">{multiple ? 'Chosen Files' : 'Chosen File'}</Text>
            <Stack
              horizontal
              verticalAlign="center"
              styles={{ root: { maxWidth: '100%', overflowX: 'auto', padding: '30px 0' } }}
              tokens={{ childrenGap: 15 }}
            >
              {files.map(({ name }, i) => (
                <StackItem key={`${name}-${i}`} styles={{ root: { textAlign: 'center', position: 'relative' } }}>
                  <Icon className={classNames.remove} iconName="RemoveFilter" onClick={removeFile(i)} />
                  <Icon iconName="OpenFile" styles={{ root: { fontSize: 35 } }} />
                  <br />
                  <Text nowrap styles={{ root: { display: 'block', margin: '15px 0' } }}>
                    {name}
                  </Text>
                </StackItem>
              ))}
            </Stack>
          </>
        );
      } else {
        return (
          <>
            <Icon iconName="CloudUpload" styles={{ root: { fontSize: 50 } }} />
            <Text
              styles={{
                root: {
                  marginTop: '10px',
                  marginBottom: '10px',
                  fontSize: FontSizes.small,
                  fontWeight: FontWeights.semiBold,
                },
              }}
            >
              Drag and Drop {multiple ? 'files' : 'a file'} here
            </Text>
            {extensions?.length && (
              <Text styles={{ root: { fontSize: FontSizes.xxsmall } }}>File supported: {extensions?.join(', ')}</Text>
            )}
            <input
              id="file"
              className={classNames.input}
              onChange={onChange}
              type="file"
              accept={extensions?.join(',')}
              multiple={multiple}
            />
            <Button styles={buttonStylesPrimary} text="Browse file..." htmlFor="file" />
            {maxFileSize || maxSize ? (
              <Text styles={{ root: { marginTop: '4px', fontSize: FontSizes.xxsmall } }}>
                {maxFileSize
                  ? `Maximum file size: ${maxFileSize}MB${maxSize ? ` (total: ${maxSize}MB)` : ''}`
                  : `Maximum total file size: ${maxSize}MB`}
              </Text>
            ) : (
              ''
            )}
          </>
        );
      }
    };

  const style =
    isDragging && !err && !successMsg ? { borderColor: theme.semanticColors?.fileUploadActiveBorder } : undefined;
  return (
    <div
      {...elementProps}
      ref={ref}
      data-test={props['data-test'] || 'file-upload'}
      className={classNames.root}
      onDragStart={onIsDragging}
      onDragEnter={onIsDragging}
      onDragEnd={onIsNotDragging}
      onDragLeave={onIsNotDragging}
      onDrop={onDrop}
      onDragOver={onDragOver}
      style={style}
    >
      {getUploadBodyComponent()}
    </div>
  );
});
